import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { employees } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { vacationOrder, type VacationType } from '@/api/vacation';
import PrintActions from '@/shared/components/PrintActions';

const TYPE_LABELS: Record<VacationType, string> = {
  ANNUAL_BASIC: 'щорічна основна',
  ANNUAL_ADDITIONAL: 'щорічна додаткова',
  SOCIAL: 'соціальна',
  UNPAID: 'без збереження заробітної плати',
  CREATIVE: 'творча',
};

export default function VacationOrderPrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['vacation-order', id ?? ''],
    queryFn: () => vacationOrder.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const empQ = useQuery({
    queryKey: ['employee-full', docQ.data?.employee_id ?? ''],
    queryFn: () => employees.get(docQ.data!.employee_id),
    enabled: !!docQ.data,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (!docQ.data || !empQ.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const d = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === d.organization_id);
  const dt = (s: string) => new Date(s).toLocaleDateString('uk-UA');

  return (
    <div className="print-page" style={{
      padding: '15mm', fontFamily: 'Times, serif', fontSize: '10pt',
      maxWidth: '210mm', margin: '0 auto', background: 'var(--bg-panel)', lineHeight: 1.4,
    }}>
      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.vacation_orders',
        documentId: d.id,
        filename: `Наказ про відпустку ${d.document_number ?? ''}`.trim(),
        orgName: org?.name ?? null,
      }} />

      <div style={{ textAlign: 'center' }}>
        <div>{org?.full_name ?? org?.name ?? ''}</div>
        {org?.edrpou && <div style={{ fontSize: '9pt' }}>ЄДРПОУ: {org.edrpou}</div>}
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '14pt', margin: '1.5rem 0 0.3rem' }}>
        НАКАЗ № {d.document_number}
      </h2>
      <p style={{ textAlign: 'center', margin: 0 }}>від {dt(d.document_date)}</p>
      <p style={{ textAlign: 'center', margin: '0.5rem 0', fontWeight: 600 }}>
        про надання відпустки
      </p>

      <p style={{ marginTop: '1.5rem' }}>
        Надати <strong>{empQ.data.full_name}</strong>
        {empQ.data.code ? ` (таб. № ${empQ.data.code})` : ''}
        {empQ.data.position_name ? `, ${empQ.data.position_name}` : ''}
        {empQ.data.department_name ? ` (${empQ.data.department_name})` : ''} відпустку
        (<strong>{TYPE_LABELS[d.vacation_type]}</strong>) тривалістю&nbsp;
        <strong>{d.days_count}</strong> календарних днів
        {' '}з <strong>{dt(d.vacation_start_date)}</strong>
        {' '}по <strong>{dt(d.vacation_end_date)}</strong>.
      </p>

      {d.reason && (
        <p style={{ marginTop: '0.5rem' }}>
          <strong>Підстава:</strong> {d.reason}
        </p>
      )}

      <table style={{ width: '100%', marginTop: '3rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%' }}>
              <div>Керівник:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(підпис, П.І.Б.)</div>
            </td>
            <td style={{ width: '50%' }}>
              <div>З наказом ознайомлений:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>
                (підпис працівника, дата)
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <style>{`
        @media print {
          .print-hide { display: none !important; }
        }
      `}</style>
    </div>
  );
}
